import { InteractionType } from "@azure/msal-browser";
import type { AuthenticationConfig } from "@sdeapps/react-core";
import { frLocale } from "@sdeapps/react-core";
import storageService from "services/storageService";

const APPLICATION_NAME = "Eau Secours";
const DEFAULT_PAGE_TITLE = "Eau Secours";

const config = {
  basename: import.meta.env.PUBLIC_PATH,
  isBrowser: typeof window !== "undefined",
  appName: APPLICATION_NAME,
  appVersion: import.meta.env.VITE_VERSION ?? "?",
};

export type RouteDefinition = {
  path: string;
  getParameterPath: (...parameters: Array<string>) => string;
  title: string;
  handle?: { breadcrumb: string };
};
/**
 * Clés des routes déclarées / déclarables.
 * La syntaxe `.. as const` permet d'avoir un tableau immuable (enum like)
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const declaredRoutesKeys = [
  "home",

  "accident",
  "accidentComplement",
  "accidentPhotos",

  "securite",
  "securitePhotos",

  "dangerGrave",
  "dangerGravePhotos",

  "new",

  "accidentBenin",
  "accidentTravail",
  "accidentTrajet",
  "presquAccident",
  "situationDangereuse",

  "photo",

  "legal",
  "enSavoirPlus",

  "any",
] as const;
/** Type d'union des clés de routes possibles afin de typer nos `routesConfig` */
type RoutesConfigKey = (typeof declaredRoutesKeys)[number];
type RoutesConfig = Record<RoutesConfigKey, RouteDefinition>;
const routesConfig: RoutesConfig = {
  home: { path: "/", getParameterPath: (_) => "/", title: DEFAULT_PAGE_TITLE },

  accident: {
    path: "/accident/:declarationId",
    getParameterPath: (declarationId) => `/accident/${declarationId}`,
    title: "Consultation : Informations générales",
  },
  accidentComplement: {
    path: "/accident/complement/:declarationId",
    getParameterPath: (declarationId) => `/accident/complement/${declarationId}`,
    title: "Consultation : Informations complémentaires",
  },
  securite: {
    path: "/securite/:declarationId",
    getParameterPath: (declarationId) => `/securite/${declarationId}`,
    title: "Consultation",
  },

  accidentPhotos: {
    path: "/accident/:declarationId/photos",
    getParameterPath: (declarationId) => `/accident/${declarationId}/photos`,
    title: "Photos",
  },
  securitePhotos: {
    path: "/securite/:declarationId/photos",
    getParameterPath: (declarationId) => `/securite/${declarationId}/photos`,
    title: "Photos",
  },
  dangerGravePhotos: {
    path: "/danger/:declarationId/photos",
    getParameterPath: (declarationId) => `/danger/${declarationId}/photos`,
    title: "Photos",
  },

  new: { path: "/new", getParameterPath: (_) => "/new", title: "Nouvelle Déclaration" },
  accidentBenin: {
    path: "/accident-benin",
    getParameterPath: (_) => "/accident-benin",
    title: "Nouvel Accident Bénin",
  },
  accidentTravail: {
    path: "/accident-travail",
    getParameterPath: (_) => "/accident-travail",
    title: "Nouvel Accident de travail",
  },
  accidentTrajet: {
    path: "/accident-trajet",
    getParameterPath: (_) => "/accident-trajet",
    title: "Nouvel Accident de trajet",
  },
  presquAccident: {
    path: "/presqu-accident",
    getParameterPath: (_) => "/presqu-accident",
    title: "Nouveau Presqu'accident",
  },
  situationDangereuse: {
    path: "/situation-dangereuse",
    getParameterPath: (_) => "/situation-dangereuse",
    title: "Nouvelle Situation dangereuse",
  },
  dangerGrave: {
    path: "/danger",
    getParameterPath: (_) => "/danger",
    title: "Nouveau Danger grave et imminent",
  },

  photo: {
    path: `photo/:photoUrl`,
    getParameterPath: (photoUrl) => `photo/${photoUrl}`,
    title: "Photo",
  },
  legal: { path: "/legal", getParameterPath: (_) => "/legal", title: "Mentions Légales" },
  enSavoirPlus: {
    path: "/en_savoir_plus",
    getParameterPath: (_) => "/en_savoir_plus",
    title: "En savoir plus",
  },
  any: { path: "*", getParameterPath: (_) => "*", title: "Not Found" },
};

const authenticationConfig: AuthenticationConfig = {
  msal: {
    auth: {
      clientId: import.meta.env.VITE_AUTH_CLIENT_ID,
      authority: import.meta.env.VITE_AUTH_AUTHORITY,
      knownAuthorities: [import.meta.env.VITE_AUTH_AUTHORITY],
      redirectUri: `${import.meta.env.VITE_APP_BASE_URL}/redirect.html`,
      postLogoutRedirectUri: `${import.meta.env.VITE_APP_BASE_URL}/redirect.html`,
      ...frLocale,
    },
    cache: { cacheLocation: "localStorage" },
  },
  graph: {
    graphEndpoint: "https://graph.microsoft.com/v1.0",
    scopes: ["User.Read", "User.ReadBasic.All", "User.Read.All"],
  },
  storage: {
    clear: storageService.clearLocalAndSessionStorage,
  },
  interactionType: InteractionType.Silent,
};

export interface SDEApi {
  scopes: Array<string>;
  apiName: string;
}

const apiConfig: {
  apimBaseUrl: string;
  apimSubscriptionKey: string;
  declarationApi: SDEApi;
  ouvragesApi: SDEApi;
  azureAd: {
    scopes: Array<string>;
  };
} = {
  apimBaseUrl: import.meta.env.VITE_APIM_BASE_URL,
  apimSubscriptionKey: import.meta.env.VITE_APIM_SUBSCRIPTION_KEY,
  declarationApi: {
    scopes: import.meta.env.VITE_DECLARATIONS_SCOPES?.split(" "),
    apiName: import.meta.env.VITE_DECLARATIONS_API_NAME,
  },
  ouvragesApi: {
    scopes: import.meta.env.VITE_OUVRAGES_SCOPES?.split(" "),
    apiName: import.meta.env.VITE_OUVRAGES_API_NAME,
  },
  azureAd: {
    scopes: ["User.Read", "User.ReadBasic.All", "User.Read.All"],
  },
};

const appiConfig = {
  connectionString: import.meta.env.VITE_APPI_CONNECTION_STRING,
};

const envConfig = {
  name: import.meta.env.VITE_ENV,
};

const localStorageKeys: Record<string, `sdeapps.secu.${string}`> = {
  RGPD: "sdeapps.secu.isAcceptingRGPD",
  introText: "sdeapps.secu.introText",
  currentUserData: "sdeapps.secu.currentUserData",
};

const oneDayMs = 24 * 60 * 60 * 1000; // 1 jour en millisecondes
const defaultCacheMaxAge = 100 * oneDayMs; // 100 jours
const cacheConfig = {
  ouvrages: {
    name: "ouvrages",
    matchUrl: new RegExp(`${apiConfig.apimBaseUrl}/${apiConfig.ouvragesApi.apiName}`, "i"),
    strategy: "NetworkFirst",
    maxAge: defaultCacheMaxAge,
  },
  territoires: {
    name: "territoires",
    matchUrl: /data\/territoires/i,
    strategy: "NetworkFirst",
    maxAge: defaultCacheMaxAge,
  },
  communes: {
    name: "communes",
    matchUrl: /https:\/\/geo\.api\.gouv\.fr\/communes/i,
    strategy: "NetworkFirst",
    maxAge: defaultCacheMaxAge,
  },
  agents: {
    name: "agents",
    matchUrl: new RegExp(`${authenticationConfig.graph.graphEndpoint}/users`, "i"),
    strategy: "NetworkFirst",
    maxAge: defaultCacheMaxAge,
  },
  env: {
    name: "env",
    matchUrl: /data\/env/i,
    strategy: "CacheFirst",
    maxAge: 365 * oneDayMs, // 1 an
  },
  microsoftlogin: {
    name: "microsoftlogin",
    matchUrl: new RegExp(import.meta.env.VITE_AUTH_AUTHORITY ?? "", "i"),
    strategy: "NetworkFirst",
    maxAge: 365 * oneDayMs, // 1 an
  },
};

export {
  config,
  authenticationConfig,
  apiConfig,
  cacheConfig,
  DEFAULT_PAGE_TITLE,
  routesConfig,
  localStorageKeys,
  appiConfig,
  envConfig,
};
